import React from "react"
import { Div } from "atomize"
import { theme } from "../settings/config"
import ConfirmButtons from "../buttons/ConfirmButtons"
import SchemaDrawing from "../lesson/schema/SchemaDrawing"
import { normalizeLessonContent } from "../utils/lessonContent.utils"
import { NOTEBOOK_EXERCISE, exercisePhaseLabel, levelLabel, usesLabel } from "../utils/notebook.utils"

// Prima di togliere una voce dal quaderno si chiede, come prima di
// eliminare una lezione: cosa sta per sparire (la sua carta: nome, fase,
// quante volte è servita, l'inizio del testo e la miniatura dello schema),
// la riga che rassicura sulle lezioni già fatte, e i tasti di sempre, con
// "No, macchè!" pieno sotto il pollice.
//
// È il contenuto di un foglio: il suo (`removeNotebookEntry`, dalla voce
// aperta di un esercizio) o un passo di quello di "Modifica", che così non
// si chiude e, tornando indietro, non perde quello che si stava cambiando.
//
// Con `variant`, si toglie solo quella variante: le altre restano. Togliendo
// la voce intera si dice quante varianti se ne vanno con lei.

const c = theme.colors

const Tag = ({ children }) => (
	<span
		style={{
			background: c.gunmetal_lighter,
			color: c.gunmetal,
			borderRadius: "10px",
			padding: "0.125rem 0.5rem",
			fontSize: "0.75rem",
			fontWeight: 500,
			whiteSpace: "nowrap",
		}}>
		{children}
	</span>
)

function NotebookRemoveConfirm({ entry: whole, variant = null, busy, onConfirm, onCancel }) {
	const entry = variant ? { ...whole, ...variant } : whole
	const isExercise = entry.kind === NOTEBOOK_EXERCISE
	const snippet = isExercise ? entry.text : normalizeLessonContent(entry.content).objective
	const variantsCount = whole.variants?.length ?? 1

	return (
		<>
			<Div m={{ b: "0.75rem" }} textWeight="bold" textSize="title">
				{variant
					? `Vuoi togliere la variante ${levelLabel(variant.level)}?`
					: isExercise
						? "Vuoi togliere l'esercizio dal quaderno?"
						: "Vuoi togliere la lezione dal quaderno?"}
			</Div>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "0.75rem",
					padding: "1rem",
					borderRadius: "20px",
					background: c.surface,
					boxShadow: theme.shadows.accent,
				}}>
				<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "0.375rem" }}>
					<div style={{ fontWeight: 700, lineHeight: 1.25, color: c.gunmetal }}>{entry.title}</div>
					<div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
						{isExercise && <Tag>{exercisePhaseLabel(entry.phase)}</Tag>}
						{variant && <Tag>{levelLabel(variant.level)}</Tag>}
						{!variant && variantsCount > 1 && <Tag>{variantsCount} varianti</Tag>}
						<Tag>{usesLabel(entry)}</Tag>
					</div>
					{snippet && (
						<div
							style={{
								fontSize: "0.8125rem",
								lineHeight: 1.35,
								color: c.text_light,
								display: "-webkit-box",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
								overflow: "hidden",
							}}>
							{snippet}
						</div>
					)}
				</div>
				{isExercise && entry.schema && (
					<span
						style={{
							flex: "none",
							width: "3.5rem",
							aspectRatio: "358 / 400",
							borderRadius: "10px",
							overflow: "hidden",
							background: c.schema_court,
						}}>
						<SchemaDrawing schema={entry.schema} grass={false} label={`Schema: ${entry.title}`} />
					</span>
				)}
			</div>
			<Div m={{ t: "0.75rem" }} textSize="caption" textAlign="center" textColor={c.text_light}>
				{variant
					? "Le altre varianti restano, e le lezioni che l'hanno usata restano come sono."
					: isExercise
						? "Le lezioni che l'hanno usato restano come sono."
						: "Le lezioni che l'hanno usata restano come sono."}
			</Div>
			<ConfirmButtons
				confirmText={isExercise && !variant ? "Sì, toglilo" : "Sì, toglila"}
				busy={busy}
				busyText={isExercise && !variant ? "Lo tolgo..." : "La tolgo..."}
				onConfirm={onConfirm}
				onCancel={onCancel}
			/>
		</>
	)
}

export default NotebookRemoveConfirm
